import appConfig from 'app/config/client';
import { OauthLoginCallback } from 'app/constants/Routes';
import OauthError from 'app/services/oauth/OauthError';
import { DEFAULT_SCOPES } from 'app/constants/enums/Ability';
import { Action, LoginIntent } from 'app/services/oauth/constants';
import { loadContinuation, saveContinuation } from 'app/services/oauth/Continuation';
import { HelloJSAuthResponse, HelloJSLoginOptions, HelloJSStatic } from 'hellojs';
import { InvalidResultError } from 'app/services/oauth/errors';
import createErrorType from 'app/utils/createErrorType';
import { IDMMAuthService, DMMLoginState } from './DMMAuthService';
import { IAuthToken } from 'app/models/auth/constants';
import { getBackendPath } from 'app/utils/paths';
import { AuthProvider, AUTH_PROVIDERS, DMM } from 'app/constants/enums/AuthProvider';
import { isUnknownObject } from 'app/utils/object';
import { EntityStringId } from 'app/types/entity';
import { createGuid } from 'app/types/guid';

export class LoginError extends createErrorType('LoginError') {
  public constructor(
    message: string,
    public readonly provider: AuthProvider,
    public readonly response: unknown,
  ) {
    super(message);
  }
}

// This split is used internally by helloJS. When you specifiy a scope
// delmiter, hellojs splits the scope then does a join using the
// specified delimiter before hitting the auth endpoint.
const SCOPE_SPLIT = /[,\s]+/;
const SCOPE_DELIM = ' ';

type ResponseWithAuthToken = IAuthToken | HelloJSAuthResponse;

const initHello = (helloInstance: HelloJSStatic) => {
  // load a module for our backend into hellojs instance
  helloInstance.init({
    backend: {
      oauth: {
        version:       '2',
        auth:          `${getBackendPath()}/api/oauth/authorize`,
        grant:         `${getBackendPath()}/api/oauth/token`,
        response_type: 'token',

      },

      refresh: false,
      base:    `${getBackendPath()}/api`,

      scope_delim: SCOPE_DELIM,
    },
  });

  // specify the client id for the backend we just added
  helloInstance.init({
    backend: appConfig.oauth.clientId,
  });
};

let helloLoadPromise: Promise<HelloJSStatic>;

export const getHello = async () => {
  if (!helloLoadPromise) {
    helloLoadPromise = (async () => {
      const result = (await import('hellojs')).default;
      initHello(result);
      return result;
    })();
  }
  return await helloLoadPromise;
};

const VALID_PROVIDERS = AUTH_PROVIDERS.toSet();

const fixHelloJSError = (e: unknown): unknown => {
  if (isUnknownObject(e) && isUnknownObject(e.error)) {
    // probably a hello.js mangled error, convert to actual error
    // have to convert hello errors to oauth errors
    return Object.assign({}, e, { error: e.error.code });
  } else {
    return e;
  }
};

interface ILoginDependencies {
  dmmAuthService: IDMMAuthService;
}

export const finishDMMLogin = async (
  result: { [k: string]: string },
  deps: ILoginDependencies,
) => {
  if (!result) {
    throw new InvalidResultError(
      'Attempt to finish dmm login without valid result');
  }

  const continuation = loadContinuation(Action.DMM_LOGIN, result.state);
  return await externalOauthLogin(continuation.next, deps);
};

export const externalOauthLogin = async (
  {
    returnUriReference,
    provider,
    extraParams,
    isReauthentication,
    intent,
    brandId,
  }: {
    returnUriReference: string,
    provider: AuthProvider,
    extraParams: { [index: string]: unknown },
    isReauthentication: boolean,
    intent: LoginIntent,
    brandId: EntityStringId,
  },
  { dmmAuthService }: ILoginDependencies,
) => {
  if (typeof returnUriReference !== 'string') {
    throw new Error(
      'returnUriReference must be a string, ' +
      `got ${returnUriReference} (${typeof returnUriReference})`);
  }

  if (!provider || !VALID_PROVIDERS.has(provider)) {
    throw new Error(
      `invalid provider: '${provider?.id}', ` +
      ` expected one of ${Array.from(VALID_PROVIDERS.map(({ id }) => id)).join(',')}`);
  }

  const state = createGuid();

  // DMM 2-phase oauth!
  if (
    provider.id === DMM.id &&
    dmmAuthService.getDMMLoginStateSync() === DMMLoginState.NO_DMM_LOGIN
  ) {
    const dmmLoginReturnState = createGuid();

    saveContinuation({
      action: Action.DMM_LOGIN,
      state:  dmmLoginReturnState,
      next: {
        returnUriReference,
        provider,
        extraParams,
        isReauthentication,
        intent,
        brandId,
      },
    });

    await dmmAuthService.auth(intent, dmmLoginReturnState);

    // TODO: this is an unfortunate implementation leak
    throw new Error('Dmm login service returned instead of redirecting!');
  } else {
    saveContinuation({
      action:               Action.AUTH,
      redirectUriReference: returnUriReference,
      provider,
      state,
      isReauthentication:   !!isReauthentication,
    });

    const loginOptions = {
      redirect_uri: OauthLoginCallback.getPathname(),

      display:  'page',
      page_uri: null, /* prevent the default from coming back here */

      provider,
      brand: brandId,
      scope: DEFAULT_SCOPES.map(({ id }) => id).join(' '),

      ...(extraParams || {}),

      state,
    };

    try {
      const hello = await getHello();
      await hello('backend').login(loginOptions as HelloJSLoginOptions);

      // HelloJS in `display: page` does not return, but redirects.
      if (loginOptions.display === 'page') {
        throw new Error('Hello returned instead of redirecting!');
      }
    } catch (e) {
      const error = fixHelloJSError(e);
      throw OauthError.tryParse(error) || error;
    }
  }
};

export type OauthLoginResult = {
  redirectUriReference: string;
  isReauthentication: boolean;
  success: boolean,
} & (
  { success: true, token: { [index: string]: unknown } } |
  { success: false, error: unknown }
);

export const finishOauthLogin = async (): Promise<OauthLoginResult> => {
  const hello = await getHello();

  const result = formatAuthResponse(hello.getAuthResponse('backend'));

  if (!result || !isHelloJSAuthResponse(result)) {
    throw new InvalidResultError(
      'Attempt to finish oauth login without valid hello.js result');
  }

  // Consume the hello state. We use hello only to obtain a token,
  // when we then own.
  void hello.logout('backend');

  const {
    provider, redirectUriReference, isReauthentication,
  } = loadContinuation(Action.AUTH, result.state || '');

  try {
    // @ts-ignore -- this works, don't know why the types don't include it.
    if (result.error) {
      const errorBody  = fixHelloJSError(result);
      const oauthError = OauthError.tryParse(errorBody, { provider });

      if (oauthError) {
        throw oauthError;
      } else {
        throw new LoginError(
          'Unexpected error response from server (could not be parsed as an oauth error)',
          provider,
          // @ts-ignore -- TODO
          result.error,
        );
      }
    }

    const { access_token, expires_in, token_type, scope } = result as IAuthToken;

    const token = {
      access_token,
      expires_in,
      token_type,
      scope,
    };

    return { success: true, token, redirectUriReference, isReauthentication };
  } catch (e) {
    // Errors returned here are to be handled in a context, and that
    // context is read from the continuation. Propagate it to our
    // caller.
    return { success: false, error: e, redirectUriReference, isReauthentication };
  }
};

const isHelloJSAuthResponse = (result: ResponseWithAuthToken): result is HelloJSAuthResponse => {
  try {
    // Only checks state as that's the only part of the hellojs response used in finishOauthLogin
    const { state } = result as HelloJSAuthResponse;
    return state !== undefined;
  } catch (e) {
    return false;
  }
};

// hello.getAuthResponse doesn't format scopes in the response using
// the scope delimiter so the returned result needs to be reformatted.
const formatAuthResponse = (response: HelloJSAuthResponse): ResponseWithAuthToken => {
  let formattedResponse: ResponseWithAuthToken | undefined;

  if (!response) {
    return response;
  } else if (response.scope) {
    formattedResponse = Object.assign({}, response);
    const formattedScope = response.scope
      .split(SCOPE_SPLIT)
      .join(SCOPE_DELIM);
    formattedResponse.scope = formattedScope;
  }
  return formattedResponse || response;
};
